import { TeamApi, UserApi, getApiConfigNext, getTeamWithRoster, updateTeamRoster, type RosterPlayer as Player, type TeamWithRoster, type Person, type Team, type TeamEdit, type UserTeams } from '@y-sports/api-client';
import type { TeamResponse } from '@y-sports/api-client/generated-next-registry/src/models';
import { TeamResponseFromJSON } from '@y-sports/api-client/generated-next-registry/src/models/TeamResponse';
import { TeamBasicStatus } from '@y-sports/api-client/generated-v2/src/models';
import type {
  CreateTeamFromTemplateRequest,
  CreateTeamRegistrationTemplateRequest,
  TeamBasic,
  TeamRegistrationTemplate,
  TeamsListResponse,
  UserTeamReference,
} from '@y-sports/api-client/generated-v2/src/models';
import { TeamBasicFromJSON } from '@y-sports/api-client/generated-v2/src/models/TeamBasic';
import { UserDashboardStatsFromJSON } from '@y-sports/api-client/generated-v2/src/models/UserDashboardStats';
import { TeamRegistrationTemplateFromJSON } from '@y-sports/api-client/generated-v2/src/models/TeamRegistrationTemplate';
import { TeamsListResponseFromJSON } from '@y-sports/api-client/generated-v2/src/models/TeamsListResponse';
import type { ResourcePermissions } from './AccessDataService';

export interface CreateTeamPayload {
  [key: string]: unknown;
}

export interface ManagedTeamOption {
  id: string;
  name: string;
  playersCount: number;
  officialsCount: number;
}

export type ManagedTeam = TeamWithRoster & {
  permissions?: ResourcePermissions | null;
};

export interface TournamentRegistrationPayload {
  ageCheckOverride?: boolean;
  existingTeamId?: string;
  teamName: string;
  clubName?: string;
  country?: string;
  state?: string;
  city?: string;
  category: string;
  gender?: string;
  primaryColor?: string;
  homeColor?: string;
  awayColor?: string;
  goalkeeperColor?: string;
  players?: Array<{
    id?: string;
    playerNumber?: number;
    person?: {
      id?: string;
      firstName?: string;
      secondName?: string;
      lastName?: string;
      birthDate?: number;
      email?: string;
      phoneNumber?: string;
    };
  }>;
  officials?: Array<{
    id?: string;
    firstName?: string;
    secondName?: string;
    lastName?: string;
    birthDate?: number;
    email?: string;
    phoneNumber?: string;
  }>;
  creator?: {
    id?: string;
    firstName?: string;
    secondName?: string;
    lastName?: string;
    birthDate?: number;
    email?: string;
    phoneNumber?: string;
  };
}

export interface TournamentTeamResponse {
  id?: string;
}

export interface ManagedTeamMatch {
  scheduledAt?: string | null;
  matchId: string;
  tournamentId: string;
  tournamentName?: string | null;
  tournamentDate?: string | null;
  categoryId?: string | null;
  categoryName?: string | null;
  matchTime?: string | null;
  groupName?: string | null;
  label?: string | null;
  opponentTeamId?: string | null;
  opponentTeamName?: string | null;
  teamScore?: number | null;
  opponentScore?: number | null;
  status?: string | null;
  result?: string | null;
  streamingUrl?: string | null;
}

export interface ManagedTeamMatchesResponse {
  upcoming: ManagedTeamMatch[];
  past: ManagedTeamMatch[];
}

export interface ManagedTeamParticipationHistoryEntry {
  id: string;
  teamId: string;
  teamNameSnapshot: string;
  eventName: string;
  eventDate: string;
  matchesPlayed: number;
  roster: string[];
  createdAt: string;
  updatedAt: string;
}

export interface ManagedTeamParticipationHistoryEntryPayload {
  eventName: string;
  eventDate: string;
  matchesPlayed: number;
  roster: string[];
}

export interface ManagedTeamDocument {
  id: string;
  teamId: string;
  fileName: string;
  description?: string | null;
  originalFileName: string;
  downloadFileName: string;
  contentType: string;
  sizeBytes: number;
  uploadedBy?: string | null;
  createdAt: string;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function normalizeUuidLike(value?: string): string | undefined {
  if (!value) {
    return undefined;
  }

  return UUID_PATTERN.test(value) ? value : undefined;
}

function sanitizePlayerPayload(player: Player): Player {
  return {
    ...player,
    id: normalizeUuidLike(player.id),
    person: player.person
      ? {
          ...player.person,
          id: normalizeUuidLike(player.person.id),
        }
      : undefined,
  };
}

function sanitizeOfficialPayload(person: Person): Person {
  return {
    ...person,
    id: normalizeUuidLike(person.id),
  };
}

async function fetchNextJson<T>(
  path: string,
  init: RequestInit,
  parse: (json: unknown) => T,
): Promise<T> {
  const apiConfig = await getApiConfigNext();
  const response = await fetch(`${apiConfig.basePath}${path}`, {
    ...init,
    headers: {
      ...(init.headers || {}),
      ...(apiConfig.headers || {}),
    },
  });

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  return parse(await response.json());
}

function createRequestHeaders(headers?: HeadersInit, dropContentType = false): Headers {
  const normalizedHeaders = new Headers(headers);

  if (dropContentType) {
    normalizedHeaders.delete('Content-Type');
    normalizedHeaders.delete('content-type');
  }

  return normalizedHeaders;
}

export const TeamDataService = {
  getManagedTeams: async (email: string): Promise<ManagedTeamOption[]> => {
    const apiConfig = await getApiConfigNext();
    const userApi = new UserApi(apiConfig);
    const userTeams = await userApi.getUserTeams({ email });

    return (userTeams || []).flatMap((group: UserTeams) =>
      (group.teams || [])
        .filter((team) => !!team.id)
        .map((team) => ({
          id: team.id!,
          name: team.name || team.id!,
          playersCount: team.players?.length || 0,
          officialsCount: team.officials?.length || 0,
        }))
    );
  },

  getOwnedTeams: async (): Promise<ManagedTeamOption[]> => {
    return fetchNextJson('/teams/mine', {
      method: 'GET',
    }, (json) => (Array.isArray(json) ? json : []).map((team) => {
      const item = team as {
        id?: string;
        name?: string | null;
        players?: unknown[] | null;
        officials?: unknown[] | null;
      };
      return {
        id: item.id ?? '',
        name: item.name || item.id || '',
        playersCount: item.players?.length ?? 0,
        officialsCount: item.officials?.length ?? 0,
      };
    }).filter((team) => !!team.id));
  },

  getAllTeamsSummary: async (): Promise<TeamBasic[]> => {
    const teams = await fetchNextJson('/registry/teams', {
      method: 'GET',
    }, (json) => (Array.isArray(json) ? json.map((item) => TeamResponseFromJSON(item)) : [] as TeamResponse[]));

    return (teams || []).map((team) => ({
      id: team.id,
      name: team.name,
      clubName: team.clubName ?? undefined,
      status: undefined,
      country: team.countryCode ?? undefined,
      state: team.state ?? undefined,
      city: team.city ?? undefined,
    }));
  },

  getTournamentTeamsSummary: async (tournamentId: string): Promise<TeamsListResponse> => {
    return fetchNextJson(`/tournaments/${tournamentId}/teams-summary`, {
      method: 'GET',
    }, (json) => TeamsListResponseFromJSON(json));
  },

  updateTournamentTeamStatus: async (tournamentId: string, teamId: string, status: TeamBasicStatus): Promise<TeamBasic> => {
    const apiConfig = await getApiConfigNext();
    const response = await fetch(`${apiConfig.basePath}/tournaments/${tournamentId}/teams/${teamId}/status`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(apiConfig.headers || {}),
      },
      body: JSON.stringify({ status }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update team status (${response.status})`);
    }

    return response.json() as Promise<TeamBasic>;
  },

  getTeamById: async (teamId: string): Promise<ManagedTeam> => {
    return getTeamWithRoster(teamId) as Promise<ManagedTeam>;
  },

  getTeamMatches: async (teamId: string): Promise<ManagedTeamMatchesResponse> => {
    return fetchNextJson(`/teams/${teamId}/matches`, {
      method: 'GET',
    }, (json) => json as ManagedTeamMatchesResponse);
  },

  getParticipationHistory: async (teamId: string): Promise<ManagedTeamParticipationHistoryEntry[]> => {
    return fetchNextJson(`/teams/${teamId}/participation-history`, {
      method: 'GET',
    }, (json) => (Array.isArray(json) ? json as ManagedTeamParticipationHistoryEntry[] : []));
  },

  getTeamDocuments: async (teamId: string): Promise<ManagedTeamDocument[]> => {
    return fetchNextJson(`/teams/${teamId}/documents`, {
      method: 'GET',
    }, (json) => (Array.isArray(json) ? json as ManagedTeamDocument[] : []));
  },

  uploadTeamDocument: async (
    teamId: string,
    payload: { fileName: string; description?: string; file: File },
  ): Promise<ManagedTeamDocument> => {
    const apiConfig = await getApiConfigNext();
    const formData = new FormData();
    formData.append('fileName', payload.fileName);
    if (payload.description?.trim()) {
      formData.append('description', payload.description.trim());
    }
    formData.append('file', payload.file);

    const response = await fetch(`${apiConfig.basePath}/teams/${teamId}/documents`, {
      method: 'POST',
      headers: createRequestHeaders(apiConfig.headers, true),
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Failed to upload team document (${response.status})`);
    }

    return response.json() as Promise<ManagedTeamDocument>;
  },

  downloadTeamDocument: async (teamId: string, documentId: string): Promise<Blob> => {
    const apiConfig = await getApiConfigNext();
    const response = await fetch(`${apiConfig.basePath}/teams/${teamId}/documents/${documentId}/download`, {
      method: 'GET',
      headers: createRequestHeaders(apiConfig.headers),
    });

    if (!response.ok) {
      throw new Error(`Failed to download team document (${response.status})`);
    }

    return response.blob();
  },

  viewTeamDocument: async (teamId: string, documentId: string): Promise<Blob> => {
    const apiConfig = await getApiConfigNext();
    const response = await fetch(`${apiConfig.basePath}/teams/${teamId}/documents/${documentId}/view`, {
      method: 'GET',
      headers: createRequestHeaders(apiConfig.headers),
    });

    if (!response.ok) {
      throw new Error(`Failed to view team document (${response.status})`);
    }

    return response.blob();
  },

  replaceTeamDocumentContent: async (teamId: string, documentId: string, file: File): Promise<ManagedTeamDocument> => {
    const apiConfig = await getApiConfigNext();
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${apiConfig.basePath}/teams/${teamId}/documents/${documentId}/content`, {
      method: 'POST',
      headers: createRequestHeaders(apiConfig.headers, true),
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Failed to replace team document (${response.status})`);
    }

    return response.json() as Promise<ManagedTeamDocument>;
  },

  deleteTeamDocument: async (teamId: string, documentId: string): Promise<void> => {
    const apiConfig = await getApiConfigNext();
    const response = await fetch(`${apiConfig.basePath}/teams/${teamId}/documents/${documentId}`, {
      method: 'DELETE',
      headers: createRequestHeaders(apiConfig.headers),
    });

    if (!response.ok) {
      throw new Error(`Failed to delete team document (${response.status})`);
    }
  },

  createParticipationHistoryEntry: async (
    teamId: string,
    payload: ManagedTeamParticipationHistoryEntryPayload,
  ): Promise<ManagedTeamParticipationHistoryEntry> => {
    return fetchNextJson(`/teams/${teamId}/participation-history`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }, (json) => json as ManagedTeamParticipationHistoryEntry);
  },

  updateParticipationHistoryEntry: async (
    teamId: string,
    entryId: string,
    payload: ManagedTeamParticipationHistoryEntryPayload,
  ): Promise<ManagedTeamParticipationHistoryEntry> => {
    return fetchNextJson(`/teams/${teamId}/participation-history/${entryId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }, (json) => json as ManagedTeamParticipationHistoryEntry);
  },

  deleteParticipationHistoryEntry: async (teamId: string, entryId: string): Promise<void> => {
    const apiConfig = await getApiConfigNext();
    const response = await fetch(`${apiConfig.basePath}/teams/${teamId}/participation-history/${entryId}`, {
      method: 'DELETE',
      headers: {
        ...(apiConfig.headers || {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to delete participation history entry (${response.status})`);
    }
  },

  updateTeamBasicInfo: async (teamId: string, teamEdit: TeamEdit): Promise<Team> => {
    const apiConfig = await getApiConfigNext();
    const api = new TeamApi(apiConfig);
    return api.editTeam({ id: teamId, teamEdit });
  },

  updateTeamPlayers: async (teamId: string, players: Player[]): Promise<Player[]> => {
    return updateTeamRoster(teamId, players.map(sanitizePlayerPayload));
  },

  updateTeamOfficials: async (teamId: string, officials: Person[]): Promise<Person[]> => {
    const apiConfig = await getApiConfigNext();
    const api = new TeamApi(apiConfig);
    return api.updateTeamOfficials({ id: teamId, person: officials.map(sanitizeOfficialPayload) });
  },

  registerTeamInTournament: async (tournamentId: string, payload: TournamentRegistrationPayload): Promise<TournamentTeamResponse> => {
    const apiConfig = await getApiConfigNext();
    const response = await fetch(`${apiConfig.basePath}/tournaments/${tournamentId}/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(apiConfig.headers || {}),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Failed to register team (${response.status})`);
    }

    return response.json() as Promise<TournamentTeamResponse>;
  },

  createTeamFromTemplate: async (payload: CreateTeamFromTemplateRequest) => {
    return fetchNextJson('/teams/from-template', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }, (json) => TeamBasicFromJSON(json));
  },

  getActiveTeamRegistrationTemplates: async (): Promise<TeamRegistrationTemplate[]> => {
    return fetchNextJson('/team-registration-templates', {
      method: 'GET',
    }, (json) => (Array.isArray(json) ? json.map((item) => TeamRegistrationTemplateFromJSON(item)) : []));
  },

  getAdminTeamRegistrationTemplates: async (includeInactive = true): Promise<TeamRegistrationTemplate[]> => {
    return fetchNextJson(`/admin/team-registration-templates?include_inactive=${String(includeInactive)}`, {
      method: 'GET',
    }, (json) => (Array.isArray(json) ? json.map((item) => TeamRegistrationTemplateFromJSON(item)) : []));
  },

  createTeamRegistrationTemplate: async (
    payload: CreateTeamRegistrationTemplateRequest,
  ): Promise<TeamRegistrationTemplate> => {
    return fetchNextJson('/admin/team-registration-templates', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }, (json) => TeamRegistrationTemplateFromJSON(json));
  },

  updateTeamRegistrationTemplate: async (
    templateId: string,
    payload: CreateTeamRegistrationTemplateRequest,
  ): Promise<TeamRegistrationTemplate> => {
    return fetchNextJson(`/admin/team-registration-templates/${templateId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }, (json) => TeamRegistrationTemplateFromJSON(json));
  },

  updateTeamRegistrationTemplateStatus: async (
    templateId: string,
    active: boolean,
  ): Promise<TeamRegistrationTemplate> => {
    return fetchNextJson(`/admin/team-registration-templates/${templateId}/status`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ active }),
    }, (json) => TeamRegistrationTemplateFromJSON(json));
  },

  createTeam: async (payload: CreateTeamPayload): Promise<{ id?: string }> => {
    const apiConfig = await getApiConfigNext();
    const response = await fetch(`${apiConfig.basePath}/teams`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(apiConfig.headers || {}),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Failed to create team (${response.status})`);
    }

    return response.json() as Promise<{ id?: string }>;
  },

  getUserTeamRegistrations: async (email: string, teamId?: string): Promise<UserTeamReference[]> => {
    const apiConfig = await getApiConfigNext();
    const response = await fetch(`${apiConfig.basePath}/users/${encodeURIComponent(email)}/dashboard-stats`, {
      method: 'GET',
      headers: {
        ...(apiConfig.headers || {}),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch user team registrations (${response.status})`);
    }

    const stats = UserDashboardStatsFromJSON(await response.json());

    const filtered = (stats.teamsRegistered || []).filter((teamRef) => !teamId || teamRef.teamId === teamId);

    return Array.from(
      filtered.reduce((acc, item) => {
        const key = item.tournamentId || item.tournamentName || `unknown-${acc.size}`;
        if (!acc.has(key)) {
          acc.set(key, item);
        }
        return acc;
      }, new Map<string, UserTeamReference>()).values(),
    );
  },
};
